import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {GetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import {DataFrameColumnMappingTable} from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnMappingTable';
import {isSendToWorksheetEnabled} from 'analysis-tool/outputs/OutputsUtil';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {GET_SUBSET_DATA_FRAME_QUERY, DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import QueryingResultSchemaSelector from 'results/ResultSchemaSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import ColumnMappingTable from './column-mapping-table/ColumnMappingTable';
import {updateMappings, updateResultSchema} from './DataMappingStateReducers';
import {DataMappingState, TargetSpecificMappingState} from './DataMappingTypes';
import SaveToResultsStepOutputPreview from './SaveToResultsStepOutputPreview';
import {
  COLUMN_MAPPING_CONFIG_BY_TARGET,
  REQUIRED_COLUMN_DEFS_BY_TARGET,
  SaveToResultsTarget,
} from './targetSpecificColumns';
import useDataMappingState from './useDataMappingState';
import validateSchemaMapping, {validateColumnMapping} from './validateDataMapping';
/** @package */
export const PREVIEW_ROW_LIMIT = 10;

export interface SaveToResultsConfigurationModalProps {
  clientStateId: string;
  dataFrame: GetAnalysisDataFrame;
  onRequestClose: () => void;
  onRequestSubmit: (result: {
    dataMapping: DataMappingState;
    targetSpecificMappingState: TargetSpecificMappingState | null;
  }) => void;
}

export default function SaveToResultsConfigurationModal(
  props: SaveToResultsConfigurationModalProps
): JSX.Element {
  const [dataMappingState, setDataMappingState, resetDataMappingState] = useDataMappingState(
    props.clientStateId,
    props.dataFrame.id
  );

  const validationErrors = validateSchemaMapping(dataMappingState);

  const [target, setTarget] = useState<SaveToResultsTarget | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);

  const targetColumnDefs = useMemo(
    () => (target == null ? [] : REQUIRED_COLUMN_DEFS_BY_TARGET[target]),
    [target]
  );

  const targetSpecificMappingState: TargetSpecificMappingState | null = useMemo(() => {
    if (target == null) {
      return null;
    }
    const targetColumnMappings = [];
    for (let i = 0; i < selectedColumns.length; i++) {
      const targetColumnDef = targetColumnDefs[i];
      const selectedColumnDef = selectedColumns[i];
      if (selectedColumnDef == null) {
        continue;
      }
      targetColumnMappings.push({
        inputColumnSystemName: selectedColumnDef.systemName,
        outputColumnSystemName: targetColumnDef.systemName,
      });
    }
    return {
      target,
      targetVersion: 0,
      columnMappings: targetColumnMappings,
    };
  }, [target, targetColumnDefs, selectedColumns]);

  const targetColumnValidationErrors =
    targetSpecificMappingState == null
      ? []
      : validateColumnMapping(
          targetSpecificMappingState.columnMappings,
          targetColumnDefs,
          props.dataFrame.allColumnsJson,
          {
            outputColumnConfig: COLUMN_MAPPING_CONFIG_BY_TARGET[targetSpecificMappingState.target],
          }
        );

  function onClose(): void {
    props.onRequestClose();
    resetDataMappingState();
    setTarget(null);
    setSelectedColumns([]);
  }

  function onSubmit(): void {
    props.onRequestSubmit({dataMapping: dataMappingState, targetSpecificMappingState});
    resetDataMappingState();
    setTarget(null);
    setSelectedColumns([]);
  }

  const buttons = [
    {
      text: 'Save',
      disabled:
        dataMappingState.resultSchema == null ||
        dataMappingState.mappings.length === 0 ||
        validationErrors.length > 0 ||
        targetColumnValidationErrors.filter((e) => e.level === 'ERROR').length > 0,
      submit: true,
    },
  ];

  return (
    <Modal
      buttons={buttons}
      onRequestClose={onClose}
      onRequestSubmit={onSubmit}
      title="Map to result"
      scrollable={true}
      expandHeight={true}
      fullWidth={true}
    >
      <div className={css(styles.twoPanes)}>
        <FormGroup className={css(styles.configurationSection)}>
          <FieldLabel label="Result type" />
          <QueryingResultSchemaSelector
            selectedSchema={dataMappingState.resultSchema}
            onChange={(newResultSchema) => {
              setDataMappingState(
                updateResultSchema(dataMappingState, newResultSchema, props.dataFrame.allColumnsJson)
              );
            }}
            filter={(resultSchema) => !isArchived(resultSchema)}
            requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
          />
          {dataMappingState.resultSchema && (
            <div className={css(styles.mappingSection)}>
              <ColumnMappingTable
                inputDataFrameSpec={{name: props.dataFrame.name, columns: props.dataFrame.allColumnsJson}}
                outputResultSchema={dataMappingState.resultSchema}
                mappings={dataMappingState.mappings}
                setMappings={(newMappings) => {
                  setDataMappingState(updateMappings(dataMappingState, newMappings));
                }}
                errors={validationErrors}
              />
            </div>
          )}
          {/* TODO(BNCH-107061): polish, adjust padding, wording, mapping UI */}
          {BenchlingConfig.SAMPLE_TESTS_ENABLE && isSendToWorksheetEnabled() && (
            <div className={css(styles.sampleTestSection)}>
              <FieldLabel label="Configure downstream target" />
              <div>
                <CheckboxInput
                  className={css(styles.checkbox)}
                  checked={target === SaveToResultsTarget.TEST_ORDER_TABLE}
                  onChange={(ev) => {
                    if (ev.target.checked) {
                      setTarget(SaveToResultsTarget.TEST_ORDER_TABLE);
                      setSelectedColumns([]);
                    } else {
                      setTarget(null);
                      setSelectedColumns([]);
                    }
                  }}
                  id="target-assay-checkbox"
                  label="Use this result in an assay"
                />
              </div>
              {targetSpecificMappingState != null && (
                <div>
                  <DataFrameColumnMappingTable
                    templateDataFrameColumns={targetColumnDefs}
                    newDataFrameColumns={props.dataFrame.allColumnsJson}
                    setSelectedColumns={setSelectedColumns}
                    selectedColumns={selectedColumns}
                    errors={targetColumnValidationErrors}
                  />
                </div>
              )}
            </div>
          )}
        </FormGroup>
        <div className={css(styles.preview)}>
          <PersistedDataFrameTableWrapper
            actionOrigin={ActionOrigin.ANALYSIS_TOOL}
            clientStateId={props.clientStateId}
            dataFrameId={props.dataFrame.id}
            rowLimit={PREVIEW_ROW_LIMIT}
          />
          <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
            query={GET_SUBSET_DATA_FRAME_QUERY}
            renderLoading={() => <CenteredHorizontalThrobber />}
            variables={{dataFrameId: props.dataFrame.id, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
          >
            {({dataFrame}) => {
              assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
              return (
                <SaveToResultsStepOutputPreview
                  clientStateId={`${props.clientStateId}_preview`}
                  inputDataFrame={dataFrame.dataFrameJson}
                  dataMappingState={dataMappingState}
                  targetSpecificMappingState={targetSpecificMappingState}
                />
              );
            }}
          </BenchlingQuery>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  twoPanes: {display: 'flex'},
  configurationSection: {marginRight: 16, flex: 1},
  mappingSection: {
    marginTop: 16,
    borderRadius: 4,
    border: 1,
    borderStyle: 'solid',
    borderColor: GRAY_LIGHT,
    padding: 16,
  },
  tableSpacing: {marginTop: 8},
  preview: {
    flex: 1,
    minWidth: 0,
    overflow: 'auto',
  },
  checkbox: {
    verticalAlign: 'text-top',
    marginRight: 15,
  },
  sampleTestSection: {
    marginTop: 10,
  },
});
